import * as React from 'react';
import { useEffect } from 'react';

import type { InjectedRouter } from 'js/lib/connectToRouter';
import { useLocation } from 'js/lib/useRouter';
import user from 'js/lib/user';

import IdealUpgradeEnrollModalContainer from 'bundles/coursera-plus-common/components/subscriptionTiers/upgradeToPlus/IdealUpgradeEnrollModalContainer';
import IndiaPriceTestUpgradeModal from 'bundles/coursera-plus-common/components/subscriptionTiers/upgradeToPlus/IndiaUpgrade/IndiaPriceTestUpgradeModal';
import { getIsLearnerImpressedInIndiaTiersEpic } from 'bundles/coursera-plus-common/utils/subscriptionTiersUtils';
import { useFreeTrialRemovalTreatment } from 'bundles/enroll-common/utils/freeTrialRemovalExperimentUtils';
import { canEnrollThroughFreemium } from 'bundles/enroll-common/utils/freemiumModelUtils';
import CourseraPlusEnrollModal from 'bundles/enroll-modal/components/coursera-plus/CourseraPlusEnrollModal';
import GuidedProjectEnrollModal from 'bundles/enroll-modal/components/guided-project/GuidedProjectEnrollModal';
import CourseEnrollS12nSelectionModalV2 from 'bundles/enroll-modal/components/mix-and-match/CourseEnrollS12nSelectionModalV2';
import SelfPacedProjectEnrollModal from 'bundles/enroll-modal/components/projects/SelfPacedProjectEnrollModal';
import S12nNonRecurringEnrollModal from 'bundles/enroll-modal/components/s12n-non-recurring/S12nNonRecurringEnrollModal';
import S12nPaymentMethodEnrollModal from 'bundles/enroll-modal/components/s12n-non-recurring/S12nPaymentMethodEnrollModal';
import CancelPreEnrollS12nSelectModal from 'bundles/enroll-modal/components/s12n-pre-enrollment/CancelPreEnrollS12nSelectModal';
import S12nPreEnrollModal from 'bundles/enroll-modal/components/s12n-pre-enrollment/S12nPreEnrollModal';
import S12nSubscriptionEnrollModal from 'bundles/enroll-modal/components/s12n-subscriptions/S12nSubscriptionEnrollModal';
import S12nSubscriptionEnrollModalNoFreeTrial from 'bundles/enroll-modal/components/s12n-subscriptions/S12nSubscriptionEnrollModalNoFreeTrial';
import ClosedCourseEnrollModal from 'bundles/enroll-modal/components/standalone-course/ClosedCourseEnrollModal';
import CourseWithFullDiscountEnrollModal from 'bundles/enroll-modal/components/standalone-course/CourseWithFullDiscountEnrollModal';
import StandaloneCourseFreemiumEnrollModal from 'bundles/enroll-modal/components/standalone-course/StandaloneCourseFreemiumEnrollModal';
import StandaloneCourseNonFreemiumEnrollModal from 'bundles/enroll-modal/components/standalone-course/StandaloneCourseNonFreemiumEnrollModal';
import UserInterestModal from 'bundles/enroll-modal/components/user-interest/UserInterestModal';
import { useCourseEnrollModalData } from 'bundles/enroll-modal/data/usePageData';
import useMixAndMatchOrderingTreatment from 'bundles/enroll-modal/hooks/useMixAndMatchOrderingTreatment';
import EnterpriseEnrollmentChoiceModalForXDP from 'bundles/enterprise-legacy-learner-home/components/EnterpriseEnrollmentChoiceModalForXDP';
import { PRODUCT_TYPES } from 'bundles/enterprise-legacy-learner-home/constants/ProgramActionConstants';
import paymentsExperiments from 'bundles/epic/clients/__generated__/payments';
import { CourseType } from 'bundles/naptimejs/resources/courseTypeMetadata.v1';
import { useCoursePageData } from 'bundles/unified-description-page-common/contexts/consumer/ConsumerBffCourseContext';

export type PropsFromCaller = {
  courseId: string;
  s12nId?: string;
  partnerIds?: Array<string>;
  // LO-5359: only consumed by the CourseraPlusEnrollModal implementation (the C+
  // branch below), which documents how it is used.
  enrollCourseOnly?: boolean;
  onClose: (event?: React.MouseEvent<HTMLElement>) => void;
  onS12nSelect: (s12nId: string) => void;
  // Pre Enrollment V2
  fromMultipleLearningProgramsLink?: boolean;
  router?: InjectedRouter;
};

const CourseEnrollModal = ({
  courseId,
  onClose,
  onS12nSelect,
  fromMultipleLearningProgramsLink,
  enrollCourseOnly,
}: PropsFromCaller) => {
  const {
    course,
    s12n,
    enrollmentAvailableChoices,
    courseTypeMetadataWithVersion,
    s12nDerivatives,
    s12nSubscriptionOwnableProduct,
    programs,
    thirdPartyOrganizations,
    invitedPrograms,
    invitedThirdPartyOrganizations,
    ownsCourseraLite,
    isCourseraPlusEligibleProduct,
  } = useCourseEnrollModalData();

  const { query } = useLocation();
  const pageData = useCoursePageData();
  const s12nSlugFromUrl = query?.specialization;
  const selectedS12nFromUrl = pageData?.specializations?.find((s12nItem) => s12nItem.slug === s12nSlugFromUrl);
  const preselectedS12nId = selectedS12nFromUrl?.id;

  const {
    canSubscribeToS12n,
    canEnrollThroughS12nPrepaid,
    canEnrollThroughCourseraPlus,
    didPurchase,
    isSpecializationUpgradeRequired,
    canEnrollThroughProgram,
    canEnrollCourseWithFullDiscount,
    canEnrollThroughProgramInvitation,
    canPurchaseSingleCourse,
    canPurchaseThroughCourseraPlus,
    isEnrolled,
    isMixAndMatch,
    isPreEnrolled,
    preEnrolledS12nIds,
    selectedS12nIdOverrideToEnrollThroughCourseraPlus: selectedS12nIdOverride,
    isMixMatchWithS12nsThatCannotEnrollThroughPlus,
    canEnrollThroughPlusWithSelectedS12n,
  } = enrollmentAvailableChoices;
  // When a learner arrives with a specific s12n preselected (e.g. from an SDP link), skip the
  // s12n selection step and take them straight into enrolling in that s12n. This is also passed
  // downstream (as didSkipMixAndMatchModal) so the enrollment modal can surface specialization
  // context copy when the selection step was skipped.
  const shouldSkipSelectS12nModal =
    Boolean(preselectedS12nId) && isMixAndMatch && (canSubscribeToS12n || canEnrollThroughS12nPrepaid);

  useEffect(() => {
    if (shouldSkipSelectS12nModal && preselectedS12nId) {
      onS12nSelect(preselectedS12nId);
    }
  }, [shouldSkipSelectS12nModal, preselectedS12nId, onS12nSelect]);

  const isMultiplePreEnrolledS12ns = preEnrolledS12nIds.length > 1;
  const showPreEnrollCancelModal = isMixAndMatch && isPreEnrolled && !fromMultipleLearningProgramsLink;
  const isEnterpriseUser = canEnrollThroughProgram || canEnrollThroughProgramInvitation;
  const { isClosedCourse } = course;
  const isFreemiumLearner = canEnrollThroughFreemium({
    courseId: course.id,
    enrollmentAvailableChoices,
    courseTypeMetadataWithVersion,
  });
  const isFreeTrialRemovalTreatment = useFreeTrialRemovalTreatment(canSubscribeToS12n);

  // Impression point per the experiment plan: enrolling from a course page in 2+ s12ns without `?specialization=`,
  // excluding learners who already own an s12n (prepaid) or a subscription. Course page data is only provided on
  // the consumer course page, which keeps in-course upgrade entry points out of the experiment.
  // The treatment value is consumed by the data-driven ordering + auto-skip follow-up (MONETIZE-3252).
  useMixAndMatchOrderingTreatment(
    Boolean(pageData) &&
      isMixAndMatch &&
      !s12n &&
      !s12nSlugFromUrl &&
      !showPreEnrollCancelModal &&
      !selectedS12nIdOverride &&
      canSubscribeToS12n
  );

  useEffect(() => {
    if (showPreEnrollCancelModal && !isMultiplePreEnrolledS12ns) {
      // @ts-expect-error TS2345 Unchecked index access, value is possibly undefined see https://www.typescriptlang.org/tsconfig/#noUncheckedIndexedAccess
      onS12nSelect(preEnrolledS12nIds?.[0]);
    }
  }, [showPreEnrollCancelModal, isMultiplePreEnrolledS12ns, preEnrolledS12nIds, onS12nSelect]);

  useEffect(() => {
    if (isMixAndMatch && selectedS12nIdOverride) {
      onS12nSelect(selectedS12nIdOverride);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedS12nIdOverride, isMixAndMatch]);

  if (showPreEnrollCancelModal && isMultiplePreEnrolledS12ns) {
    return (
      <CancelPreEnrollS12nSelectModal
        handleClose={onClose}
        automaticallyEnroll={isEnterpriseUser || canEnrollThroughCourseraPlus}
      />
    );
  }

  // If user can enroll into multiple s12ns, have user first select a specific s12n to enroll in
  if (isMixAndMatch && !s12n) {
    if (shouldSkipSelectS12nModal) {
      // The preselected s12n is being auto-selected; render nothing while we wait for the
      // selection to propagate so we never flash the s12n selection modal.
      return null;
    }
    return (
      <CourseEnrollS12nSelectionModalV2
        onClose={onClose}
        onSubmit={onS12nSelect}
        isTerminal={canEnrollThroughCourseraPlus}
      />
    );
  }

  const waitForBothProgramDataFetch = canEnrollThroughProgram && canEnrollThroughProgramInvitation;

  const isFromS12nSelection = isMixAndMatch && Boolean(s12n);
  const s12nId = s12n?.id;
  const shouldShowPrepaidModal =
    s12nId &&
    ((canEnrollThroughS12nPrepaid && enrollmentAvailableChoices?.prepaidEnrollmentS12nIds?.includes(s12nId)) ||
      (canPurchaseThroughCourseraPlus &&
        enrollmentAvailableChoices?.courseraPlusPrepaidEnrollmentS12nIds?.includes(s12nId)));

  const shouldShowPreEnrollModal =
    !paymentsExperiments.preview('disablePreEnrollment') &&
    s12nId &&
    enrollmentAvailableChoices.canPreEnrollOrIsPreEnrolledS12n(s12nId);

  // For the list of s12ns that are in the pre enrolled flow, show the pre enroll modal before any other s12n modal
  if (shouldShowPreEnrollModal && s12nId) {
    return (
      <S12nPreEnrollModal
        s12nId={s12nId}
        onSdp={false}
        onClose={onClose}
        courseId={courseId}
        automaticallyEnroll={isEnterpriseUser || canEnrollThroughCourseraPlus}
      />
    );
    // Show the product interest modal if the course is not launched and learner can't subscribe to the course's s12n
  } else if (
    (s12nDerivatives?.isSubscription || s12nSubscriptionOwnableProduct?.isSubscription) &&
    !course.isLaunched &&
    !canSubscribeToS12n
  ) {
    return <UserInterestModal courseId={courseId} onClose={onClose} />;
    // Only trigger Coursera Plus flow for users who are already subscribed users
  } else if (
    canEnrollThroughCourseraPlus &&
    (!isMixMatchWithS12nsThatCannotEnrollThroughPlus || canEnrollThroughPlusWithSelectedS12n(s12nId as string))
  ) {
    return <CourseraPlusEnrollModal courseId={courseId} onClose={onClose} enrollCourseOnly={enrollCourseOnly} />;
  } else if (isCourseraPlusEligibleProduct && isEnrolled && ownsCourseraLite) {
    if (getIsLearnerImpressedInIndiaTiersEpic()) {
      return <IndiaPriceTestUpgradeModal onClose={onClose} />;
    }
    return <IdealUpgradeEnrollModalContainer onClose={onClose} />;
  } else if (shouldShowPrepaidModal) {
    const PrepaidModal = enrollmentAvailableChoices?.subscriptionEnrollmentS12nIds.includes(s12nId)
      ? S12nPaymentMethodEnrollModal
      : S12nNonRecurringEnrollModal;

    return (
      <PrepaidModal
        s12nId={s12nId}
        courseId={course.id}
        onSdp={false}
        onClose={onClose}
        isFromS12nSelection={isFromS12nSelection}
        didSkipMixAndMatchModal={shouldSkipSelectS12nModal}
      />
    );
  } else if (canSubscribeToS12n) {
    if (isFreeTrialRemovalTreatment) {
      return (
        <S12nSubscriptionEnrollModalNoFreeTrial
          onClose={onClose}
          onSdp={false}
          isFromS12nSelection={isFromS12nSelection}
        />
      );
    }
    return (
      <S12nSubscriptionEnrollModal
        onClose={onClose}
        onSdp={false}
        isFromS12nSelection={isFromS12nSelection}
        didSkipMixAndMatchModal={shouldSkipSelectS12nModal}
      />
    );
  } else if (canEnrollCourseWithFullDiscount) {
    return <CourseWithFullDiscountEnrollModal courseId={courseId} onClose={onClose} />;
  }
  // Technically Guided Project is a closed course. We don't want to show the Closed course modal for this so
  // the Guided Project check should be placed before closed course.
  else if (
    courseTypeMetadataWithVersion?.courseTypeMetadata.typeName === CourseType.RHYME_PROJECT &&
    canPurchaseSingleCourse
  ) {
    return <GuidedProjectEnrollModal courseId={courseId} onClose={onClose} />;
  } else if (
    courseTypeMetadataWithVersion?.courseTypeMetadata.typeName === CourseType.PROJECT &&
    canPurchaseSingleCourse
  ) {
    return <SelfPacedProjectEnrollModal courseId={courseId} onClose={onClose} />;
  } else if (isClosedCourse && !didPurchase && !canEnrollThroughProgram && !isSpecializationUpgradeRequired) {
    return <ClosedCourseEnrollModal onClose={onClose} />;
  } else if (isEnterpriseUser) {
    return (
      <EnterpriseEnrollmentChoiceModalForXDP
        thirdPartyOrganizations={thirdPartyOrganizations}
        programs={programs}
        invitedThirdPartyOrganizations={invitedThirdPartyOrganizations}
        invitedPrograms={invitedPrograms}
        productId={course.id}
        productType={PRODUCT_TYPES.COURSE}
        courseSlug={course.slug}
        userId={user.get().id}
        handleClose={onClose}
        waitForBothProgramDataFetch={waitForBothProgramDataFetch}
      />
    );
    // This modal both handles standalone course enrollment
    // and enrolling into a course as part of an existing s12n subscription/purchase
    // TODO: separate out these two cases being handled into different components
  } else {
    // For freemium model flow
    if (isFreemiumLearner) {
      return <StandaloneCourseFreemiumEnrollModal s12nId={s12nId} onClose={onClose} />;
    }

    return <StandaloneCourseNonFreemiumEnrollModal courseId={courseId} s12nId={s12nId} onClose={onClose} />;
  }
};

export default CourseEnrollModal;
